import { ref, reactive, onActivated, onMounted, nextTick } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import { Edit, Delete } from '@element-plus/icons-vue'
import { request } from '@/util/globalContext'
import { ValidConst } from '@/util/validConst'

import { enumSelect } from '@/util/vueutil/commonList'

export default function useRoleList(roleTreeRef) {
    const configRoleDialogVisible = ref(false)
    const roleTreeData = ref([])
    const checkedRoleKeys = ref([])
    const treeProps = {
        label: 'roleName',
        children: 'children'
    }

    // 设置检车员相关变量
    const inspectorDialogVisible = ref(false)
    const inspectorLoading = ref(false)
    const inspectorUserDetail = ref(null)
    const inspectorCurrentStatus = ref(0) // 0=普通, 1=检车员

    const searchForm = reactive({
        userId: '',
        userName: '',
        isDeleted: '',
        createTime: '',
        updateTimeRange: []
    })

    const listRef = ref(null)
    const dialogVisible = ref(false)
    const dialogType = ref('add')
    const formRef = ref(null)
    const selectedRow = ref(null)

    const FORM_DEFAULT = {
        userId: '',
        userName: '',
        phoneNumber: '',
        email: '',
        wechat: '',
        isDeleted: 0,
        companyId: ''
    }
    const formData = reactive({ ...FORM_DEFAULT })

    // 公司相关变量
    const allCompanyList = ref([])
    const selectedCompanyId = ref('')
    const selectedCompanyDetail = ref(null)
    const companySelectDialogVisible = ref(false)
    const tempSelectedCompanyId = ref('')
    const companyTypeList = ref([])

    const formRules = reactive({
        userName: [
            { required: true, message: '请输入用户名称', trigger: 'blur' },
            { max: 20, message: '角色ID长度不能超过25个字符', trigger: 'blur' }
        ],
        phoneNumber: [
            { required: true, message: '请输入11位有效手机号', trigger: 'blur' },
            { pattern: ValidConst.PHONE_REG, message: ValidConst.PHONE_MSG, trigger: 'blur' }
        ],
        email: [
            { required: true, message: '请输入邮箱地址', trigger: 'blur' },
            { pattern: ValidConst.EMAIL_REG, message: ValidConst.EMAIL_MSG, trigger: 'blur' }
        ],
        roleName: [
            { required: true, message: '请输入角色名称', trigger: 'blur' },
            { max: 25, message: '角色名称长度不能超过25个字符', trigger: 'blur' }
        ],
        isDeleted: [
            { required: true, message: '请选择是否生效', trigger: 'change' }
        ],
        roleDesc: [
            { max: 100, message: '角色描述长度不能超过100个字符', trigger: 'blur' }
        ]
    })

    const handleLoadList = async (paginationParams) => {
        if (!listRef.value) return
        if (!listRef.value.isInited) {
            await nextTick()
            if (!listRef.value.isInited) {
                ElMessage.warning('列表组件初始化中，请稍后重试')
                return
            }
        }

        listRef.value.setLoading(true)
        try {
            const params = {
                ...paginationParams,
                ...searchForm,
                updateTimeStart: searchForm.updateTimeRange[0] || '',
                updateTimeEnd: searchForm.updateTimeRange[1] || ''
            }
            const res = await request.get('/user/getUserList', { params })
            if (res.code === 200) {
                listRef.value.updateListData(res.data.records || [])
                listRef.value.updatePagination({
                    pageNum: paginationParams.pageNum,
                    pageSize: paginationParams.pageSize,
                    total: res.data.total || 0
                })
            } else {
                ElMessage.error('列表加载失败：' + (res.msg || '接口异常'))
                listRef.value.updateListData([])
                listRef.value.updatePagination({ ...paginationParams, total: 0 })
            }
        } catch (error) {
            ElMessage.error('列表加载失败：网络异常')
            listRef.value.updateListData([])
            listRef.value.updatePagination({ ...paginationParams, total: 0 })
        } finally {
            listRef.value.setLoading(false)
        }
    }

    const handleSelectionChange = (val) => {
        selectedRow.value = val?.length ? val[0] : null
    }
    const handleCurrentRowChange = (val) => {
        selectedRow.value = val
    }

    const handleAdd = () => {
        dialogType.value = 'add'
        formRef.value?.resetFields()
        Object.assign(formData, FORM_DEFAULT)
        dialogVisible.value = true
    }

    // 加载所有公司列表
    const loadAllCompanies = async () => {
        try {
            // 加载公司类型枚举
            companyTypeList.value = await enumSelect('enumType', 'company_type')
            // 加载公司列表
            const res = await request.get('/home/getCompanyList')
            if (res.code === 200) {
                allCompanyList.value = res.data || []
            } else {
                allCompanyList.value = []
            }
        } catch (err) {
            console.error('获取公司列表失败', err)
            allCompanyList.value = []
        }
    }

    // 更新公司详情显示
    const updateCompanyDetail = () => {
        if (!selectedCompanyId.value) {
            selectedCompanyDetail.value = null
            return
        }
        const company = allCompanyList.value.find(c => c.companyId === selectedCompanyId.value)
        selectedCompanyDetail.value = company ? { ...company } : null
    }

    const handleEdit = async () => {
        if (!selectedRow.value) {
            ElMessage.warning('请先选中一条数据！')
            return
        }
        if (!listRef.value?.isInited) {
            ElMessage.warning('列表组件初始化中，请稍后重试')
            return
        }

        try {
            listRef.value.setLoading(true)
            // 先加载所有公司列表
            await loadAllCompanies()

            const res = await request.get('/user/getUserList', {
                params: { userId: selectedRow.value.userId }
            })

            if (res.code !== 200) {
                ElMessage.error(res.msg || '接口异常')
                return
            }
            const userData = res.data.records[0]
            formData.userId = userData.userId
            formData.userName = userData.userName
            formData.phoneNumber = userData.phoneNumber
            formData.email = userData.email
            formData.isDeleted = userData.isDeleted

            try {
                const companyRes = await request.get('/home/getUserDetail', {
                    params: { userId: userData.userId }
                })
                if (companyRes && companyRes.code === 200) {
                    formData.companyId = companyRes.data.companyId || ''
                    formData.wechat = companyRes.data.wechat || ''
                } else {
                    formData.companyId = ''
                    formData.wechat = ''
                }
            } catch (err) {
                console.error('获取公司信息失败', err)
                formData.companyId = ''
                formData.wechat = ''
            }

            // 初始化公司选择状态
            selectedCompanyId.value = formData.companyId
            tempSelectedCompanyId.value = ''
            companySelectDialogVisible.value = false
            
            // 更新公司详情显示
            updateCompanyDetail()
            dialogType.value = 'edit'
            dialogVisible.value = true
        } catch (error) {
            ElMessage.error('获取详情失败' + error)
        } finally {
            listRef.value.setLoading(false)
        }
    }

    const handleDelete = async () => {
        if (!selectedRow.value) {
            ElMessage.warning('请先选中一条角色数据！')
            return
        }
        if (selectedRow.value.isDeleted === 1) {
            ElMessage.warning('该用户已处于失效状态！')
            return
        }

        try {
            await ElMessageBox.confirm(
                '确定要失效该用户吗？',
                '失效确认',
                { confirmButtonText: '确定', cancelButtonText: '取消', type: 'warning' }
            )

            listRef.value.setLoading(true)
            const res = await request.post('/user/delUser', { userId: selectedRow.value.userId })
            if (res.code === 200) {
                ElMessage.success('失效成功')
                const pagination = listRef.value.getPagination()
                handleLoadList(pagination)
            } else {
                ElMessage.error('失效失败：' + (res.msg || '接口异常'))
            }
        } catch (error) {
            if (error !== 'cancel') {
                ElMessage.error('失效失败：网络异常')
            }
        } finally {
            listRef.value.setLoading(false)
        }
    }

    // 打开公司选择弹窗
    const openCompanySelectDialog = () => {
        tempSelectedCompanyId.value = selectedCompanyId.value
        companySelectDialogVisible.value = true
    }

    // 公司选择变化
    const handleCompanySelect = (companyId) => {
        tempSelectedCompanyId.value = companyId
        // 选择后立即更新显示
        if (companyId) {
            const company = allCompanyList.value.find(c => c.companyId === companyId)
            selectedCompanyId.value = companyId
            selectedCompanyDetail.value = company ? { ...company } : null
        } else {
            selectedCompanyId.value = ''
            selectedCompanyDetail.value = null
        }
    }

    // 确认选择公司
    const confirmCompanySelect = () => {
        if (!tempSelectedCompanyId.value) {
            ElMessage.warning('请选择公司')
            return
        }
        companySelectDialogVisible.value = false
    }

    // 取消公司选择
    const cancelCompanySelectDialog = () => {
        // 取消时恢复原来的选择
        selectedCompanyId.value = formData.companyId
        updateCompanyDetail()
        tempSelectedCompanyId.value = ''
        companySelectDialogVisible.value = false
    }

    // 解除绑定
    const handleUnbindCompany = () => {
        ElMessageBox.confirm(
            '确定要解除该公司的绑定吗？',
            '解除绑定确认',
            { confirmButtonText: '确定', cancelButtonText: '取消', type: 'warning' }
        ).then(() => {
            formData.companyId = ''
            selectedCompanyId.value = ''
            updateCompanyDetail()
            companySelectDialogVisible.value = false
        }).catch(() => {})
    }

    const handleSubmit = async () => {
        if (!formRef.value) return

        const isValid = await new Promise((resolve) => {
            formRef.value.validate((valid) => {
                resolve(valid)
            })
        })
        if (!isValid) return

        listRef.value.setLoading(true)
        try {
            // 更新companyId为选择的值
            formData.companyId = selectedCompanyId.value || ''
            const res = await request.post('/user/saveOrUpdateUser', { ...formData, dialogType: dialogType.value })
            if (res.code === 200) {
                ElMessage.success(res.msg)
                dialogVisible.value = false
                const pagination = listRef.value.getPagination()
                handleLoadList(pagination)
            } else {
                const tip = dialogType.value === 'add' ? '新增失败' : '修改失败'
                ElMessage.error(tip + '：' + (res.msg || '接口异常'))
            }
        } catch (error) {
            ElMessage.error('接口异常:' + error)
        } finally {
            listRef.value.setLoading(false)
        }
    }

    const handleReset = () => {
        Object.keys(searchForm).forEach((key) => {
            searchForm[key] = Array.isArray(searchForm[key]) ? [] : ''
        })
    }

    const configuration = async (row) => {
        if (!row.userId) {
            ElMessage.error('参数为空无法配置')
            return
        }
        try {
            listRef.value.setLoading(true)
            const res = await request.get('/user/getUserRoleTree', { params: { userId: row.userId } })
            if (res.code === 200) {
                roleTreeData.value = res.data || []
                const roleList = Array.isArray(res.data) ? res.data : []
                checkedRoleKeys.value = roleList
                    .filter(item => item.checkType === 0)
                    .map(item => item.roleId)
                    .filter(roleId => roleId)
                configRoleDialogVisible.value = true
                await nextTick()
                if (roleTreeRef.value) {
                    roleTreeRef.value.userId = row.userId
                    roleTreeRef.value.setCheckedKeys(checkedRoleKeys.value)
                }
            } else {
                ElMessage.error(res.msg || '查询失败')
            }
        } catch (error) {
            ElMessage.error('接口异常:' + error)
        } finally {
            listRef.value.setLoading(false)
        }
    }

    const roleDialogSubmit = async () => {
        if (!roleTreeRef.value) {
            ElMessage.warning('角色树组件未初始化')
            return
        }
        const checkedRoleIds = roleTreeRef.value.getCheckedKeys()
        if (!checkedRoleIds.length) {
            ElMessage.warning('请先选择角色')
            return
        }

        try {
            const res = await request.post('/user/saveOrUpdateUserRole', {
                userId: roleTreeRef.value.userId,
                roleList: checkedRoleIds
            })
            if (res.code === 200) {
                ElMessage.success('角色配置成功')
                configRoleDialogVisible.value = false
                const pagination = listRef.value.getPagination()
                handleLoadList(pagination)
            } else {
                ElMessage.error('角色配置失败：' + (res.msg || '接口异常'))
            }
        } catch (error) {
            ElMessage.error('接口异常:' + error)
        }
    }

    // ========= 设置检车员相关函数 =========

    // 打开设置检车员弹窗，用列表中选中的用户
    const handleSetInspector = async () => {
        if (!selectedRow.value) {
            ElMessage.warning('请先在列表中选择一个用户')
            return
        }
        if (selectedRow.value.isDeleted === 1) {
            ElMessage.warning('该用户已失效，无法设置检车员')
            return
        }

        const user = selectedRow.value
        inspectorDialogVisible.value = true
        inspectorLoading.value = true
        inspectorUserDetail.value = null
        inspectorCurrentStatus.value = 0

        try {
            const res = await request.get('/wxXcxCstInfo/getByPhone', { params: { phone: user.phoneNumber } })
            if (res.code === 200 && res.data) {
                inspectorUserDetail.value = res.data
                inspectorCurrentStatus.value = String(res.data.cstType) === '1' ? 1 : 0
            } else {
                inspectorUserDetail.value = null
                inspectorCurrentStatus.value = 0
            }
        } catch (error) {
            inspectorUserDetail.value = null
            inspectorCurrentStatus.value = 0
        } finally {
            inspectorLoading.value = false
        }
    }

    // 保存检车员状态（CST_TYPE 0=普通, 1=检车员, 3=核验员）
    const handleSaveInspectorStatus = async () => {
        if (!inspectorUserDetail.value || !inspectorUserDetail.value.id) {
            ElMessage.warning('该用户无微信账户数据，无法设置身份')
            return
        }
        const newStatus = inspectorCurrentStatus.value
        const statusMap = { 0: '设为普通用户', 1: '设为检车员', 3: '设为核验员' }
        const statusText = statusMap[newStatus] || '更新身份'
        try {
            await ElMessageBox.confirm(
                `确定要将该微信用户${statusText}吗？`,
                '确认操作',
                { confirmButtonText: '确定', cancelButtonText: '取消', type: 'warning' }
            )
        } catch {
            return
        }

        inspectorLoading.value = true
        try {
            const res = await request.post('/wxXcxCstInfo/updateCstType', {
                id: inspectorUserDetail.value.id,
                cstType: newStatus
            })
            if (res.code === 200) {
                ElMessage.success('操作成功')
                // 刷新详情
                inspectorUserDetail.value.cstType = newStatus
            } else {
                ElMessage.error('操作失败：' + (res.msg || '接口异常'))
            }
        } catch (error) {
            ElMessage.error('操作失败：网络异常')
        } finally {
            inspectorLoading.value = false
        }
    }

    onMounted(async () => {
        await nextTick()
        setTimeout(() => {
            if (listRef.value?.isInited) {
                const pagination = listRef.value.getPagination()
                handleLoadList(pagination)
            }
        }, 50)
    })
    onActivated(() => {
        if (listRef.value?.isInited) {
            const pagination = listRef.value.getPagination()
            handleLoadList(pagination)
        }
    })

    return {
        searchForm,
        listRef,
        dialogVisible,
        dialogType,
        formRef,
        selectedRow,
        formData,
        formRules,
        Edit, Delete,
        handleSelectionChange,
        handleCurrentRowChange,
        handleAdd,
        handleEdit,
        handleDelete,
        handleSubmit,
        handleLoadList,
        handleReset,
        configuration,
        configRoleDialogVisible,
        roleTreeData,
        treeProps,
        roleDialogSubmit,
        checkedRoleKeys,
        // 公司相关
        allCompanyList,
        selectedCompanyId,
        selectedCompanyDetail,
        companySelectDialogVisible,
        tempSelectedCompanyId,
        openCompanySelectDialog,
        handleCompanySelect,
        handleUnbindCompany,
        cancelCompanySelectDialog,
        confirmCompanySelect,
        // 设置检车员相关
        inspectorDialogVisible,
        inspectorLoading,
        inspectorUserDetail,
        inspectorCurrentStatus,
        handleSetInspector,
        handleSaveInspectorStatus
    }
}
